import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "react-query";
import type { CampaignFilters, CampaignType, Recipient } from "../types/ICampaign";
import { previewRecipientCount, previewRecipients } from "../api/campaigns";

export function useRecipientPreview(
  filters: CampaignFilters,
  campaignType: CampaignType,
  isOpen: boolean,
  activeTab: "filters" | "recipients",
  // Locally-excluded recipients, applied to the COUNT only. The server
  // intersects exclusions with the actual recipient set, so stale ids (e.g.
  // carried over by cloning) never subtract from the number. The recipients
  // list stays un-excluded so the "Removed" section can offer re-add.
  excludeCustomerIdsForCount: number[] | null = null
) {
  const [debouncedFilters, setDebouncedFilters] =
    useState<CampaignFilters>(filters);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [filters]);

  const countFilters = useMemo<CampaignFilters>(
    () =>
      excludeCustomerIdsForCount && excludeCustomerIdsForCount.length > 0
        ? { ...debouncedFilters, excludeCustomerIds: excludeCustomerIdsForCount }
        : debouncedFilters,
    [debouncedFilters, excludeCustomerIdsForCount]
  );

  const { data: countData, isLoading: isCountLoading } = useQuery(
    ["preview-recipient-count", countFilters, campaignType],
    () => previewRecipientCount(countFilters, campaignType),
    { enabled: isOpen }
  );

  const { data: recipientsData, isLoading: isRecipientsLoading } =
    useQuery<Recipient[]>(
      ["preview-recipients", debouncedFilters, campaignType],
      () => previewRecipients(debouncedFilters, campaignType),
      { enabled: isOpen && activeTab === "recipients" }
    );

  return {
    liveCount: countData?.count ?? null,
    liveSkipped: countData?.skipped ?? null,
    isLiveCountLoading: isCountLoading,
    recipients: recipientsData || [],
    isRecipientsLoading,
  };
}
